import { useEffect, useId } from "react";
import { useBorder } from "../../../components/hooks";
import { colorToString } from "../../../components/utils/colorToString";
import { useResponsiveStyle } from "../../../components/utils/responsive-style";
import {
  StyleControlProperty
} from "../../../controls";
import { useStyle } from "../use-style";
import { lengthPercentageDataToString } from "../../../css/length-percentage";
import { marginPropertyDataToStyle } from "../../../css/margin";
import { paddingPropertyDataToStyle } from "../../../css/padding";
import { getBox } from "../../../box-model";
import deepEqual from "../../../utils/deepEqual";
const defaultMargin = {
  marginTop: 0,
  marginRight: "auto",
  marginBottom: 0,
  marginLeft: "auto"
};
const defaultPadding = {
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0
};
function useStyleControlCssObject(style, controlDefinition) {
  const { properties } = controlDefinition.config;
  return {
    ...properties.includes(StyleControlProperty.Width) && {
      maxWidth: "100%"
    },
    ...useResponsiveStyle(
      [
        style?.width,
        style?.margin,
        style?.padding,
        useBorder(style?.border),
        style?.borderRadius,
        style?.textStyle
      ],
      ([width, margin, padding, border, borderRadius, textStyle]) => ({
        ...properties.includes(StyleControlProperty.Width) && {
          width: widthToString(width) ?? "100%"
        },
        ...properties.includes(StyleControlProperty.Margin) && marginPropertyDataToStyle(margin ?? defaultMargin, defaultMargin),
        ...properties.includes(StyleControlProperty.Padding) && paddingPropertyDataToStyle(padding ?? defaultPadding, defaultPadding),
        ...properties.includes(StyleControlProperty.Border) && {
          borderTop: borderSideToString(border?.borderTop) ?? "0 solid black",
          borderRight: borderSideToString(border?.borderRight) ?? "0 solid black",
          borderBottom: borderSideToString(border?.borderBottom) ?? "0 solid black",
          borderLeft: borderSideToString(border?.borderLeft) ?? "0 solid black"
        },
        ...properties.includes(StyleControlProperty.BorderRadius) && {
          borderTopLeftRadius: borderRadiusToString(borderRadius?.borderTopLeftRadius) ?? 0,
          borderTopRightRadius: borderRadiusToString(borderRadius?.borderTopRightRadius) ?? 0,
          borderBottomRightRadius: borderRadiusToString(borderRadius?.borderBottomRightRadius) ?? 0,
          borderBottomLeftRadius: borderRadiusToString(borderRadius?.borderBottomLeftRadius) ?? 0
        },
        ...properties.includes(StyleControlProperty.TextStyle) && {
          ...textStyle?.fontFamily && { fontFamily: `"${textStyle.fontFamily}"` },
          ...textStyle?.letterSpacing && { letterSpacing: textStyle.letterSpacing },
          ...textStyle?.fontSize && { fontSize: fontSizeToString(textStyle.fontSize) },
          ...textStyle?.fontWeight && { fontWeight: textStyle.fontWeight },
          textTransform: textStyle?.textTransform ?? [],
          fontStyle: textStyle?.fontStyle ?? []
        }
      })
    )
  };
  function widthToString(widthProperty) {
    if (widthProperty == null)
      return null;
    return lengthPercentageDataToString(widthProperty);
  }
  function borderSideToString(borderSide) {
    if (borderSide == null)
      return null;
    const { width, color, style: style2 } = borderSide;
    return `${width != null ? width : 0}px ${style2} ${color != null ? colorToString(color) : "black"}`;
  }
  function borderRadiusToString(borderRadius) {
    if (borderRadius == null)
      return null;
    return lengthPercentageDataToString(borderRadius);
  }
  function fontSizeToString(fontSize) {
    return `${fontSize.value}${fontSize.unit}`;
  }
}
function useFormattedStyle(styleControlData, controlDefinition, control) {
  const style = useStyleControlCssObject(styleControlData, controlDefinition);
  const guid = useId().replaceAll(":", "");
  const styleClassName = useStyle(style);
  const classNames = `${styleClassName} ${guid}`;
  useEffect(() => {
    let currentBoxModel = null;
    const handleAnimationFrameRequest = () => {
      if (control == null)
        return;
      const element = document.querySelector(`.${guid}`);
      const measuredBoxModel = element == null ? null : getBox(element);
      if (!deepEqual(currentBoxModel, measuredBoxModel)) {
        currentBoxModel = measuredBoxModel;
        control.changeBoxModel(currentBoxModel);
      }
      animationFrameHandle = requestAnimationFrame(handleAnimationFrameRequest);
    };
    let animationFrameHandle = requestAnimationFrame(handleAnimationFrameRequest);
    return () => {
      cancelAnimationFrame(animationFrameHandle);
      control?.changeBoxModel(null);
    };
  }, [guid, control]);
  return classNames;
}
export {
  useFormattedStyle
};
//# sourceMappingURL=style.js.map